import React, { useContext, useEffect, useId, useRef, useState, useCallback } from 'react'
import { useIntl } from 'react-intl'

// components
import SalonsCategoryPageHead from './components/SalonsCategoryPageHead/SalonsCategoryPageHead'
import SalonsSeoSchema from '../../components/SalonsSeoSchema/SalonsSeoSchema'
import SalonsView from '../../components/SalonsView/SalonsView'
import Filters from '../../components/Filters/Filters'
import SalonsAndCitiesSearchWrap from '../../components/SalonsAndCitiesSearchWrap/SalonsAndCitiesSearchWrap'
import SalonsPageBanner from './components/SalonsPageBanner/SalonsPageBanner'
import SubcategoriesNavigation from './components/SubcategoriesNavigation/SubcategoriesNavigation'
import VisuallyHidden from '../../atoms/VisuallyHidden/VisuallyHidden'
import SkipLink from '../../atoms/SkipLink/SkipLink'

// types
import {
	Currency,
	GetSalonsDataParams,
	CategoryResponse,
	ConfigResponse,
	GetSalonsFilterCountResponse,
	SalonsResponse,
	LoadSalonsFromFiltersParams,
	SalonsCategoryPageQuery,
	CmsSalonsCategoryPageData,
	Cosmetic,
	Language,
	SalonsListPageQuery,
	PageProps,
	SalonsCategoryPageContainerQuery,
	CategoriesSeoSlugResponse,
	CitiesSeoSlugResponse,
	SalonsCategoryMetaQueryParams,
	CityByPlaceId
} from '../../types/types'

// styles
import * as SC from './SalonsCategoryPageStyles'

// utils
import createPageContainer from '../../utils/createPageContainer'
import { AppContext } from '../../utils/appProvider'
import { MyLocationContext } from '../../utils/myLocationProvider'
import { areReservationsAllowed, getCategoryIDs, isNil, PAGE_LINKS, resolveAppLocale } from '../../utils/helper'
import { pushToDataLayer } from '../../utils/dataLayer'
import { getPageViewEvent, getViewItemListEvent } from '../../utils/dataLayerEvents'
import { shopsConfig, shopsConfigById } from '../../appDefaults'
import { GET_MY_LOCATION_OPTION } from '../../components/SalonsAndCitiesSearch/SalonsAndCitiesSearch'
import { ABORT_KEYS, SESSION_STORAGE_KEYS } from '../../utils/enums'
import { guidRegex } from '../../utils/regex'

// hooks
import useMessages from '../../hooks/useMessages'

// types
type RouteProps = {
	categorySlug: string
	citySlug?: string
}

type SalonsCategoryPageData = {
	salonsData: SalonsResponse
	categoryData: CategoryResponse
	configData: ConfigResponse
	filterCountsData: GetSalonsFilterCountResponse
	allowedReservations: boolean
	cmsData: CmsSalonsCategoryPageData
	currency: Currency
	currencySecondary: Currency | undefined
	primaryCategorySlug: string
	primaryCitySlug: string | undefined
	citiesSeoSlugData: CitiesSeoSlugResponse | undefined
	cityByPlaceIdData: CityByPlaceId | undefined
	categoriesSeoSlugData: CategoriesSeoSlugResponse | undefined
}

type SalonsCategoryPageProps = PageProps<SalonsCategoryPageData, SalonsCategoryPageQuery, RouteProps>

// page
const SalonsCategoryPage = (props: SalonsCategoryPageProps) => {
	const {
		setPageData,
		query,
		setQuery,
		categorySlug,
		salonsData,
		categoryData,
		configData,
		cmsData,
		currency,
		currencySecondary,
		filterCountsData,
		allowedReservations,
		citiesSeoSlugData,
		categoriesSeoSlugData,
		primaryCategorySlug,
		primaryCitySlug,
		cityByPlaceIdData
	} = props

	const { salons, pagination } = salonsData

	const { messages } = useMessages()
	const { locale, formatMessage } = useIntl()
	const { apiBrowser } = useContext(AppContext)
	const { myLocation, isLoadingMyLocation } = useContext(MyLocationContext)
	const refineHeadingId = useId()
	const resultsHeadingId = useId()

	const [cosmetics, setCosmetics] = useState<Cosmetic[]>([])
	const [languages, setLanguages] = useState<Language[]>([])

	const [initialLocationBasedLoading, setInitialLocationBasedLoading] = useState(isLoadingMyLocation)
	const [areSalonsLoading, setAreSalonsLoading] = useState<boolean>(false)
	const [isButtonLoading, setIsButtonLoading] = useState<boolean>(false)
	const [initialClientDataLoading, setInitialClientDataLoading] = useState(true)
	const [filterStatusMessage, setFilterStatusMessage] = useState('')

	const isMountFetchRef = useRef(true)
	const isMyPositionKnownOnMount = useRef(!!myLocation)
	const mergeWithPreviousSalons = useRef(false)
	const wasMapViewRef = useRef(!!query.isMapView)

	const defaultCity = configData.rolloutCountries.find((country) => country.languageCode === locale)?.capitalCity

	const MY_LOCATION_OPTION = GET_MY_LOCATION_OPTION()

	let lat = citiesSeoSlugData?.place.latitude
	let lon = citiesSeoSlugData?.place.longitude

	if (typeof query.lat === 'number' && typeof query.lon === 'number') {
		lat = query.lat
		lon = query.lon
	}

	const refetchSalonsAndFilterCountsFromLoadMoreBtn = () => {
		setIsButtonLoading(true)

		// set mergeWithPreviousSalons flag to true so the currently showed salons can be merged with newly loaded salons
		mergeWithPreviousSalons.current = true

		setQuery({
			...query,
			page: query.page + 1
		})
	}

	const getLocationParams = (city: LoadSalonsFromFiltersParams['city']) => {
		const isUsingMyPosition = city?.placeID === MY_LOCATION_OPTION.value

		let latParam: number | undefined
		let lonParam: number | undefined
		let latMy: number | undefined
		let lonMy: number | undefined
		let googlePlaceID: string | undefined

		if (myLocation || isUsingMyPosition) {
			latMy = myLocation?.latMy || city?.latitude
			lonMy = myLocation?.lonMy || city?.longitude
		}

		if (!isUsingMyPosition && city !== null) {
			latParam = city?.latitude ?? query.lat
			lonParam = city?.longitude ?? query.lon
			googlePlaceID = city?.placeID ?? query.googlePlaceID
		}

		return { lat: latParam, lon: lonParam, latMy, lonMy, googlePlaceID, isUsingMyPosition }
	}

	const updateQueryFromFilters = async (params: LoadSalonsFromFiltersParams) => {
		const {
			openingHoursStatus: openingHoursStatusParam,
			city,
			categoryIDs: categoryIDsParam,
			orderBy: orderByParam,
			exactRating: exactRatingParam,
			languageIDs: languageIDsParam,
			cosmeticIDs: cosmeticIDsParam,
			serviceTotalPriceFrom: serviceTotalPriceFromParam,
			serviceTotalPriceTo: serviceTotalPriceToParam,
			hasAvailableReservationSystem: hasAvailableReservationSystemParam,
			// NOTE: gift filter temporary commented out https://goodrequest.atlassian.net/browse/NOT-16945
			// hasGift: hasGiftParam,
			avResTimeSlotDayPart: avResTimeSlotDayPartParam,
			avResTimeSlotDate: avResTimeSlotDateParam,
			avResTimeSlotDateFrom: avResTimeSlotDateFromParam,
			avResTimeSlotDateTo: avResTimeSlotDateToParam
		} = params

		const openingHoursStatus = openingHoursStatusParam || query.openingHoursStatus
		const categoryIDs = categoryIDsParam || query.categoryIDs
		const orderBy = orderByParam || query.orderBy
		const exactRating = exactRatingParam || query.exactRating
		const languageIDs = languageIDsParam || query.languageIDs
		const cosmeticIDs = cosmeticIDsParam || query.cosmeticIDs
		const avResTimeSlotDayPart = avResTimeSlotDayPartParam || query.avResTimeSlotDayPart

		// using null to purposely reset value in following params
		const serviceTotalPriceFrom = serviceTotalPriceFromParam === null ? undefined : (serviceTotalPriceFromParam ?? query.serviceTotalPriceFrom)
		const serviceTotalPriceTo = serviceTotalPriceToParam === null ? undefined : (serviceTotalPriceToParam ?? query.serviceTotalPriceTo)
		const avResTimeSlotDate = avResTimeSlotDateParam === null ? undefined : avResTimeSlotDateParam || query.avResTimeSlotDate
		const avResTimeSlotDateFrom = avResTimeSlotDateFromParam === null ? undefined : avResTimeSlotDateFromParam || query.avResTimeSlotDateFrom
		const avResTimeSlotDateTo = avResTimeSlotDateToParam === null ? undefined : avResTimeSlotDateToParam || query.avResTimeSlotDateTo
		const hasAvailableReservationSystem =
			hasAvailableReservationSystemParam !== undefined ? hasAvailableReservationSystemParam : query.hasAvailableReservationSystem
		// NOTE: gift filter temporary commented out https://goodrequest.atlassian.net/browse/NOT-16945
		// const hasGift = hasGiftParam !== undefined ? hasGiftParam : query.hasGift

		const { lat: latParam, lon: lonParam, googlePlaceID } = getLocationParams(city)

		// update query
		setQuery({
			openingHoursStatus,
			page: 1,
			lat: latParam,
			lon: lonParam,
			categoryIDs,
			googlePlaceID,
			orderBy,
			exactRating,
			languageIDs,
			cosmeticIDs,
			serviceTotalPriceFrom,
			serviceTotalPriceTo,
			hasAvailableReservationSystem,
			// NOTE: gift filter temporary commented out https://goodrequest.atlassian.net/browse/NOT-16945
			// hasGift: hasGift || undefined,
			avResTimeSlotDayPart,
			avResTimeSlotDate,
			avResTimeSlotDateFrom,
			avResTimeSlotDateTo
		})
	}

	const fetchSalonsAndFilterCounts = useCallback(
		async (mergeWithPrevious = false) => {
			const categoryIDs = getCategoryIDs(query.categoryIDs, categoryData.category.id)

			let commonParams: GetSalonsDataParams = {
				openingHoursStatus: query.openingHoursStatus,
				latMy: myLocation?.latMy,
				lonMy: myLocation?.lonMy,
				lat,
				lon,
				categoryIDs,
				exactRating: query.exactRating,
				hasAvailableReservationSystem: query.hasAvailableReservationSystem,
				// NOTE: gift filter temporary commented out https://goodrequest.atlassian.net/browse/NOT-16945
				// hasGift: query.hasGift,
				languageIDs: query.languageIDs,
				cosmeticIDs: query.cosmeticIDs,
				orderBy: query.orderBy,
				avResTimeSlotDayPart: query.avResTimeSlotDayPart,
				avResTimeSlotDate: query.avResTimeSlotDate,
				avResTimeSlotDateFrom: query.avResTimeSlotDateFrom,
				avResTimeSlotDateTo: query.avResTimeSlotDateTo
			}

			if (!isNil(query.serviceTotalPriceFrom) || !isNil(query.serviceTotalPriceTo)) {
				commonParams = {
					...commonParams,
					serviceTotalPriceFrom: query.serviceTotalPriceFrom,
					serviceTotalPriceTo: query.serviceTotalPriceTo,
					serviceTotalPriceCurrencyCode: currency.code
				}
			}

			let latestAddedSalons: SalonsResponse['salons'] = []

			const { data, isCancelTokenError } = await setPageData(
				[
					{
						dataKey: 'salonsData',
						data: () =>
							apiBrowser.b2c.getSalonsData(
								{ ...commonParams, page: query.page },
								{ allowAbort: true, abortSignalKey: ABORT_KEYS.SALONS_ABORT_KEY }
							)
					},
					{
						dataKey: 'filterCountsData',
						data: () =>
							apiBrowser.b2c.getSalonsFilterCounts(
								{ ...commonParams, serviceTotalPriceCurrencyCode: currency.code },
								{ allowAbort: true, abortSignalKey: ABORT_KEYS.SALONS_FILTER_COUNT_ABORT_KEY }
							)
					}
				],
				{
					onSuccess: (prevData, newData) => {
						// newData.salonsData is the just-fetched page (not the merged list)
						latestAddedSalons = newData.salonsData.salons

						if (!mergeWithPrevious) {
							return newData
						}

						const mergedData = {
							...prevData,
							salonsData: {
								...prevData.salonsData,
								pagination: newData.salonsData.pagination,
								salons: [...prevData.salonsData.salons, ...newData.salonsData.salons]
							},
							filterCountsData: {
								...newData.filterCountsData
							}
						}

						return mergedData
					}
				}
			)

			if (data?.success === true) {
				// push to dataLayer
				const event = getViewItemListEvent({
					currentPage: query.page || 1,
					currentSortingOptionName: query.orderBy,
					salons: latestAddedSalons
				})
				pushToDataLayer(event)

				return {
					isCancelTokenError,
					success: true as const,
					totalCount: data.salonsData.pagination.totalCount,
					loadedCount: latestAddedSalons.length
				}
			}

			return { isCancelTokenError, success: false as const }
		},
		[
			apiBrowser,
			query.page,
			query.openingHoursStatus,
			query.orderBy,
			query.exactRating,
			query.categoryIDs,
			lat,
			lon,
			query.languageIDs,
			query.cosmeticIDs,
			query.serviceTotalPriceFrom,
			query.serviceTotalPriceTo,
			query.hasAvailableReservationSystem,
			// NOTE: gift filter temporary commented out https://goodrequest.atlassian.net/browse/NOT-16945
			// query.hasGift,
			query.avResTimeSlotDayPart,
			query.avResTimeSlotDate,
			query.avResTimeSlotDateFrom,
			query.avResTimeSlotDateTo,
			currency.code,
			categoryData.category.id,
			myLocation?.latMy,
			myLocation?.lonMy,
			setPageData
		]
	)

	useEffect(() => {
		/**
		 * Track map/list view for the fetch effect (skip "filtered" loading copy when returning from map).
		 * View-change announcement lives in SalonsView (polite live region + persistent toggle).
		 */
		if (query.isMapView) {
			wasMapViewRef.current = true
		}
	}, [query.isMapView])

	useEffect(() => {
		/**
		 * Fetch new data whenever filters/query change (or "load more") after the first data load.
		 * Announce loading (filter vs load-more), result count, or error via the status live region
		 * so screen readers hear the async list update while focus stays on the control.
		 */
		;(async () => {
			if (!isMountFetchRef.current && !query.isMapView) {
				const isLoadMore = mergeWithPreviousSalons.current
				const returningFromMap = wasMapViewRef.current

				if (isLoadMore) {
					setFilterStatusMessage(messages['Loading more salons'])
				} else if (!returningFromMap) {
					setFilterStatusMessage(messages['Loading filtered salons'])
				}

				setAreSalonsLoading(true)
				const result = await fetchSalonsAndFilterCounts(isLoadMore)

				if (!result.isCancelTokenError) {
					setAreSalonsLoading(false)
					setIsButtonLoading(false)

					if (result.success) {
						if (isLoadMore) {
							setFilterStatusMessage(
								formatMessage(
									{ id: '{count, plural, one {# more salon was loaded} other {# more salons were loaded}}' },
									{ count: result.loadedCount }
								)
							)
						} else if (!returningFromMap) {
							// Skip result-count after map→list; SalonsView already moved focus to the view toggle.
							setFilterStatusMessage(
								result.totalCount === 0
									? messages['No results found']
									: formatMessage({ id: '{count, plural, one {# salon found} other {# salons found}}' }, { count: result.totalCount })
							)
						}
					} else {
						setFilterStatusMessage(messages['Something went wrong. Data was not loaded.'])
					}
				}
				mergeWithPreviousSalons.current = false
				wasMapViewRef.current = false
			}
		})()
	}, [fetchSalonsAndFilterCounts, query.isMapView, messages, formatMessage])

	useEffect(() => {
		/**
		 * Salons are already fetched on the server side, so client-side fetching is not always necessary on page load.
		 * However, since we are working with `myLocation`, which is not available on the server, we need to handle location-based fetching.
		 * If the user grants permission to access their location, we want to refetch the data using the user's location.
		 * During this process, we show an initial loading state until the location is resolved, and if needed, fetch new data.
		 * If the user's location is already known from query parameters at page initialization, there is no need to show the initial loading state.
		 * The loading state is displayed over the salons fetched from the server, ensuring that the data is available in the HTML for search engine bots, while preventing a content flash for users.
		 * Do not update the filter status live region here — this is bootstrap, not a user-driven filter change.
		 */
		;(async () => {
			if (isLoadingMyLocation || !isMountFetchRef.current || query.isMapView) {
				return
			}

			let isCancelTokenError = false

			if (myLocation && !isMyPositionKnownOnMount.current) {
				isCancelTokenError = (await fetchSalonsAndFilterCounts()).isCancelTokenError
			}

			if (!isCancelTokenError) {
				setInitialLocationBasedLoading(false)
			}
			isMountFetchRef.current = false
		})()
	}, [isLoadingMyLocation, myLocation, fetchSalonsAndFilterCounts, query.isMapView])

	useEffect(() => {
		;(async () => {
			try {
				const [cosmeticsData, languagesData] = await Promise.all([apiBrowser.b2c.getCosmeticsData({}), apiBrowser.b2c.getLanguagesData()])

				setLanguages(languagesData.languages)
				setCosmetics(cosmeticsData.cosmetics)
			} finally {
				setInitialClientDataLoading(false)
			}
		})()
	}, [apiBrowser])

	useEffect(() => {
		// GA page view event
		const pageViewEvent = getPageViewEvent('salons', 'salons')
		pushToDataLayer(pageViewEvent)
	}, [])

	useEffect(() => {
		// remove helper data for GA event getViewItemListEvent from session storage when page unmounts or is refreshed
		// check getViewItemListEvent function for more info
		const onPageLeave = () => sessionStorage.setItem(SESSION_STORAGE_KEYS.IS_SALONS_SEARCH, '')

		window.addEventListener('beforeunload', onPageLeave)

		return () => {
			window.removeEventListener('beforeunload', onPageLeave)
			onPageLeave()
		}
	}, [])

	const getBreadcrumbItems = (hasSalonsBreadcrumb: boolean, queryParams?: SalonsListPageQuery) => {
		const items = []

		if (hasSalonsBreadcrumb) {
			items.push({ link: PAGE_LINKS['/salons'](locale), name: messages.Salons })
		}

		items.push({
			link: PAGE_LINKS['/salons/all'](locale, { ...queryParams, latMy: myLocation?.latMy, lonMy: myLocation?.lonMy }),
			name: messages['List of salons']
		})

		if (primaryCitySlug) {
			items.push(
				{
					link: PAGE_LINKS['/salons/all/:categorySlug'](locale, categorySlug, { ...query, latMy: myLocation?.latMy, lonMy: myLocation?.lonMy }),
					name: categoryData.category.name ?? ''
				},
				{ link: '', name: cityByPlaceIdData?.name ?? '' }
			)
		} else {
			items.push({ link: '', name: categoryData.category.name ?? '' })
		}

		return items
	}

	const seoSchemaBreadcrumbItems = getBreadcrumbItems(true)

	const { categoryIDs: _, ...filteredQuery } = query
	const filterQueryParams: SalonsListPageQuery = { ...myLocation, ...filteredQuery, page: 1 }
	const breadcrumbItems = getBreadcrumbItems(false, filterQueryParams)

	return (
		<>
			<SalonsSeoSchema breadcrumbItems={seoSchemaBreadcrumbItems} />
			<SalonsCategoryPageHead
				pagination={pagination}
				categoriesSeoSlugData={categoriesSeoSlugData}
				citiesSeoSlugData={citiesSeoSlugData}
				metaData={cmsData.metaData}
				primaryCategorySlug={primaryCategorySlug}
				primaryCitySlug={primaryCitySlug}
				categoryID={categoryData.category.id}
			/>
			<SC.Container>
				<SC.SalonsPageWrapper>
					<SC.Grid>
						<SC.SalonsPageBreadcrumbs breadcrumbItems={breadcrumbItems} hasSalonsBreadcrumb />
						<SalonsPageBanner cmsData={cmsData.bannersData} />
						<SC.MainTitle>{cmsData.metaData.pageTitle}</SC.MainTitle>
						<section aria-labelledby={refineHeadingId}>
							<VisuallyHidden as='h2' id={refineHeadingId}>
								{messages['Refine results']}
							</VisuallyHidden>
							<SalonsAndCitiesSearchWrap
								query={query}
								loadSalonsFromFilters={updateQueryFromFilters}
								currentCategory={{ slug: categorySlug, id: categoryData.category.id }}
								citiesSeoSlugData={citiesSeoSlugData}
								cityByPlaceIdData={cityByPlaceIdData}
							/>
							<SkipLink href='#salon-results'>{messages['Skip to salon results']}</SkipLink>
							<SubcategoriesNavigation
								categoryData={categoryData}
								categorySlug={categorySlug}
								query={query}
								updateQueryFromFilters={updateQueryFromFilters}
							/>
							<Filters
								query={query}
								filterCounts={filterCountsData}
								currency={currency}
								currencySecondary={currencySecondary}
								cosmetics={cosmetics}
								languages={languages}
								loadSalonsFromFilters={updateQueryFromFilters}
								initialClientDataLoading={initialClientDataLoading}
								topLevelCategoryID={categoryData.category.id}
								maxAvResTimeSlotDateRange={configData.maxAvResTimeSlotDateRange}
								lat={lat}
								lon={lon}
							/>
						</section>
						<section id='salon-results' aria-labelledby={resultsHeadingId} tabIndex={-1}>
							<VisuallyHidden as='h2' id={resultsHeadingId}>
								{messages['Salon results']}
							</VisuallyHidden>
							<VisuallyHidden as='div' role='status' aria-live='polite' aria-atomic='true'>
								{filterStatusMessage}
							</VisuallyHidden>
							<SalonsView
								salons={salons}
								pagination={pagination}
								categoryData={categoryData}
								categorySlug={categorySlug}
								query={query}
								setQuery={setQuery}
								loadSalonsFromLoadMoreButton={refetchSalonsAndFilterCountsFromLoadMoreBtn}
								loadSalonsFromFilters={updateQueryFromFilters}
								initialLoading={initialLocationBasedLoading}
								salonsLoading={areSalonsLoading}
								buttonLoading={isButtonLoading}
								currencyCode={currency.code}
								defaultCity={defaultCity}
								allowedReservations={allowedReservations}
								cmsData={{
									appPromoData: cmsData.appPromoData,
									smallBannerData: cmsData.salonListSmallBannerData,
									metaData: cmsData.metaData
								}}
								lat={lat}
								lon={lon}
							/>
						</section>
					</SC.Grid>
				</SC.SalonsPageWrapper>
			</SC.Container>
		</>
	)
}

// page container
const SalonsCategoryPageContainer = createPageContainer<SalonsCategoryPageData, SalonsCategoryPageContainerQuery, RouteProps>({
	identifier: 'SalonsCategoryPage',
	PageComponent: SalonsCategoryPage,
	initDefaultData: async ({ api, query, categorySlug, citySlug, locale, shopId }) => {
		const { countryCode } = shopsConfigById[shopId]

		const isCategorySlugGuid = guidRegex.test(categorySlug)

		// If the category slug is a GUID, we skip fetching categoriesSeoSlugData.
		// A GUID slug indicates that the slug is either missing in the CMS
		// or there was a problem retrieving it from the CMS in our BE
		// in both cases categoryID is used as fallback instead
		const getCategoriesSeoSlugDataPromise = isCategorySlugGuid ? undefined : api.b2c.getCategoriesSeoSlugData(categorySlug, { countryCode })
		const getConfigDataPromise = api.b2c.getConfigData()

		const salonsCategoryQuery: SalonsCategoryMetaQueryParams = {
			countryCode,
			citySlug,
			categorySlug
		}

		let categoriesSeoSlugData: CategoriesSeoSlugResponse | undefined
		let configData: SalonsCategoryPageData['configData']
		let citiesSeoSlugData: SalonsCategoryPageData['citiesSeoSlugData'] | undefined

		let primaryCitySlug: string | undefined

		let lat: number | undefined
		let lon: number | undefined

		if (!citySlug && query.googlePlaceID) {
			// Redirect candidate: category URL with googlePlaceID may map to a CMS city page.
			// Lightweight cities fetch only — full metadata is loaded later with categoryData.
			const { cities } = await api.cms.getCitiesByCategory({ countryCode, categorySlug })

			const foundCitySlug = cities?.find((city) => city.city.placeID === query.googlePlaceID)?.slugs[0]?.slugValue

			if (foundCitySlug) {
				// there are city specific pages created in the CMS, that are on its own url
				// for example "/salony/vsechny/damske-kadernictvi/praha"
				// we take the googlePlaceID from queryParameter and check if there is a city specific page on its own url
				// if there is such page, we redirect to that page
				// otherwise we stay on the category page and load the data
				return {
					redirect: {
						redirectPath: PAGE_LINKS['/salons/all/:categorySlug/:citySlug'](resolveAppLocale(locale), categorySlug, foundCitySlug, {
							...query,
							googlePlaceID: undefined,
							lat: undefined,
							lon: undefined
						}),
						redirectType: 'temporary'
					}
				}
			}
		}

		if (citySlug) {
			;[categoriesSeoSlugData, configData, citiesSeoSlugData] = await Promise.all([
				getCategoriesSeoSlugDataPromise,
				getConfigDataPromise,
				api.b2c.getCitiesSeoSlugData(citySlug, { countryCode })
			])

			if (!citiesSeoSlugData) {
				throw new Error(`CitiesSeoSlugData not found for city slug "${citySlug}"`)
			}

			primaryCitySlug = citiesSeoSlugData.primarySeoSlugsByCountry[countryCode.toLowerCase()]

			if (!primaryCitySlug) {
				throw new Error(`City slug "${citySlug}" not found in primarySeoSlugsByCountry for country "${countryCode}"`)
			}
			lat = citiesSeoSlugData.place.latitude
			lon = citiesSeoSlugData.place.longitude
		} else {
			;[categoriesSeoSlugData, configData] = await Promise.all([getCategoriesSeoSlugDataPromise, getConfigDataPromise])

			lat = query.lat
			lon = query.lon
		}

		const rootCategoryID = isCategorySlugGuid ? categorySlug : categoriesSeoSlugData?.category?.id

		if (!rootCategoryID) {
			throw new Error(`Root category "${rootCategoryID}" not found`)
		}

		const primaryCategorySlug = categoriesSeoSlugData?.category?.primarySeoSlugsByCountry[countryCode.toLowerCase()] || rootCategoryID

		const localeCode = shopsConfig.find((config) => config.lang === locale)?.locale
		const countryData = configData.rolloutCountries.find((country) => country.languageCode === localeCode)
		const currencyCode = countryData?.currencyCode
		const currency = configData.systemCurrencies.find((c) => c.code === currencyCode)

		const currencyCodeSecondary = countryData?.currencyCodeSecondary
		const currencySecondary = configData.systemCurrencies.find((c) => c.code === currencyCodeSecondary)

		if (!currency) {
			throw new Error('Currency not found')
		}

		const allowedReservations = areReservationsAllowed(configData.allowReservations, shopId)

		const categoryIDs = query.categoryIDs ? getCategoryIDs(query.categoryIDs, rootCategoryID) : undefined

		let getSalonsDataParams: GetSalonsDataParams = {
			openingHoursStatus: query.openingHoursStatus,
			latMy: query.latMy,
			lonMy: query.lonMy,
			lat,
			lon,
			categoryIDs,
			exactRating: query.exactRating,
			languageIDs: query.languageIDs,
			cosmeticIDs: query.cosmeticIDs,
			hasAvailableReservationSystem: query.hasAvailableReservationSystem,
			// NOTE: gift filter temporary commented out https://goodrequest.atlassian.net/browse/NOT-16945
			// hasGift: query.hasGift,
			orderBy: query.orderBy,
			avResTimeSlotDayPart: query.avResTimeSlotDayPart,
			avResTimeSlotDate: query.avResTimeSlotDate,
			avResTimeSlotDateFrom: query.avResTimeSlotDateFrom,
			avResTimeSlotDateTo: query.avResTimeSlotDateTo
		}

		if (!isNil(query.serviceTotalPriceFrom) || !isNil(query.serviceTotalPriceTo)) {
			getSalonsDataParams = {
				...getSalonsDataParams,
				serviceTotalPriceFrom: query.serviceTotalPriceFrom,
				serviceTotalPriceTo: query.serviceTotalPriceTo,
				serviceTotalPriceCurrencyCode: currency.code
			}
		}

		const categoryData = await api.b2c.getCategoryData(rootCategoryID)

		const [cmsData, salonsData, filterCountsData, cityByPlaceIdData] = await Promise.all([
			api.cms.getSalonsCategoryPageCmsData(salonsCategoryQuery, categoryData.category.name ?? '', categoryData.category.id),
			api.b2c.getSalonsData({
				...getSalonsDataParams,
				page: query.page
			}),
			api.b2c.getSalonsFilterCounts({ ...getSalonsDataParams, serviceTotalPriceCurrencyCode: currency.code }),
			citiesSeoSlugData ? api.b2c.getCityNameByPlaceId(citiesSeoSlugData.place.googlePlaceID) : undefined
		])

		return {
			salonsData,
			categoryData,
			configData,
			currency,
			filterCountsData,
			cmsData,
			allowedReservations,
			currencySecondary,
			citiesSeoSlugData,
			primaryCategorySlug,
			primaryCitySlug,
			categoriesSeoSlugData,
			cityByPlaceIdData
		}
	}
})

export default SalonsCategoryPageContainer
